/**
 * The admin API's integration routes (`omnium_admin/routers/integrations.py`).
 *
 * An integration connects one Scout job to one workflow's import command, for
 * one event. Work that needs a Scout run — try, run, pull, replay — is queued
 * and done by the scheduler; `now` on try and pull does it in the request.
 */

import { apiFetch } from "@/lib/api/auth";
import { ApiError, requestJson } from "@/lib/api/client";
import { getAdminApiBaseUrl } from "@/lib/api/config";
import type { ColumnInfo, FieldSetting, Settings } from "@/lib/integrations/settings";

const enc = encodeURIComponent;

/** The team's Scout. Its screens and its API share this one address. */
const SCOUT = "https://build-intelligence.dev";

/** Where Scout's own screens live, for "Open in Scout". */
export function scoutScreenUrl(): string {
  const raw = (import.meta.env.VITE_SCOUT_UI_URL as string | undefined) ?? SCOUT;
  return raw.replace(/\/$/, "");
}

export function scoutJobUrl(jobId: string, chat = false): string {
  return `${scoutScreenUrl()}/jobs/${enc(jobId)}${chat ? "/chat" : ""}`;
}

/** The Scout address new integrations use unless a person changes it. */
export const DEFAULT_SCOUT_BASE = SCOUT;

// --------------------------------------------------------------------------- shapes

export type IntegrationState = "setting_up" | "on" | "paused" | "paused_by_guard" | "off";

export type Integration = {
  id: string;
  code: string;
  name: string;
  eventCode: string;
  eventName: string;
  eventTimezone?: string;
  workflow: string;
  importCommand: string;
  scoutBaseUrl: string;
  scoutJobId: string | null;
  hasDoorbell: boolean;
  state: IntegrationState | string;
  pauseReason: string | null;
  checkSeconds: number;
  build: string | null;
  liveVersion: number | null;
  draftVersion: number | null;
  lastRunAt: string | null;
  lastCheckedAt: string | null;
  lastOutcome: string | null;
  lastError: string | null;
  openAnswers: number;
  working?: string | null;
  needsUpdate?: string[];
};

export type Version = {
  number: number;
  status: "draft" | "live" | "archived" | string;
  settings: unknown;
  note: string | null;
  createdBy: string | null;
  publishedBy: string | null;
  publishedAt: string | null;
  createdAt: string | null;
  updatedAt?: string | null;
};

export type IntegrationDetail = Integration & { live: Version | null; draft: Version | null };

export type NewIntegration = {
  code: string;
  name: string;
  event: string;
  importCommand: string;
  scoutBaseUrl: string;
  scoutJobId?: string | null;
  checkSeconds?: number;
  build?: "all" | "day" | null;
  settings?: Partial<Settings> | Record<string, unknown>;
};

export type IntegrationPatch = {
  name?: string;
  scoutBaseUrl?: string;
  scoutJobId?: string | null;
  checkSeconds?: number;
  build?: "all" | "day" | null;
  state?: "on" | "paused" | "off";
};

export type ImportFieldInfo = {
  key: string;
  label: string;
  type: string;
  required: boolean;
  hint: string;
  aliases: string[];
  many: boolean;
};

export type FieldsInfo = {
  command: string;
  workflow: string;
  fields: ImportFieldInfo[];
  matchOptions: string[];
  rowsPerCommand: number | null;
};

export type Sample = {
  runId: string | null;
  startedAt: string | null;
  rowsCollected: number;
  columns: ColumnInfo[];
  items: unknown[];
};

export type PreviewRow = {
  position: number;
  outcome: string;
  key: string | null;
  value: Record<string, unknown>;
  problems: string[];
  hold: string | null;
};

export type FieldPreview = {
  key: string;
  rows: { before: unknown; after: unknown; problem: string | null }[];
  filled: number;
  total: number;
};

export type Preview = {
  runId: string | null;
  rowsCollected: number;
  counts: Record<string, number>;
  rows: PreviewRow[];
  notes: string[];
  field: FieldPreview | null;
  missing: string[];
};

export type Suggested = { fields: Record<string, FieldSetting>; unmapped: string[] };

export type QueueJob = {
  id: number;
  kind: string;
  status: string;
  params: Record<string, unknown>;
  error: string | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
};

export type Run = {
  id: string;
  version: number | null;
  scoutRunId: string | null;
  scoutStartedAt: string | null;
  via: string;
  status: string;
  rowsCollected: number;
  counts: Record<string, number>;
  guard: string | null;
  message: string | null;
  actor: string | null;
  createdAt: string | null;
  finishedAt: string | null;
};

export type RunRow = {
  position: number;
  subjectKey: string | null;
  subjectId: string | null;
  outcome: string;
  message: string | null;
  mapped: Record<string, unknown> | null;
  raw: unknown;
};

export type RunDetail = { run: Run; rows: RunRow[]; total: number };

export type Answer = {
  id: string;
  kind: string;
  key: string;
  payload: Record<string, unknown>;
  status: string;
  answer: Record<string, unknown> | null;
  answeredBy: string | null;
  answeredAt: string | null;
  runId: string | null;
  createdAt: string | null;
  /** What answering did, in words. Only on the answer to a POST. */
  message?: string | null;
};

export type ScoutRun = {
  id: string;
  status: string;
  rows: number;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  startedBy: string | null;
  score: number | null;
};

export type ScoutSchedule = {
  timing?: string;
  timezone?: string;
  enabled?: boolean;
  next_run_at?: string | null;
};

export type ScoutJob = {
  id: string;
  name: string;
  websiteUrl: string | null;
  status: string;
  createdAt: string | null;
  lastRun: ScoutRun | null;
  lastPassed: ScoutRun | null;
  usedBy: string[];
};

export type ScoutJobDetail = ScoutJob & {
  instructions: string | null;
  schedule: ScoutSchedule | null;
  runs: ScoutRun[];
};

export type ScoutRunJoined = ScoutRun & {
  omnium: { runId: string; status: string; via: string } | null;
};

export type LogLine = { id: number; at: string; type: string; say: string; level: "info" | "warning" | "error" };

export type RunLog = { run: ScoutRun | null; log: LogLine[]; error: string | null };

export type ChatMessage = { id: string; sender: string; content: string; created_at: string };

export type Approval = {
  id: string;
  question: string;
  proposed_change: Record<string, unknown>;
  status: string;
  decided_at: string | null;
  created_at: string;
};

export type LinkEntity = "participant" | "sport" | "venue" | "fixture";

export type LinkRow = {
  externalId: string;
  internalId: string;
  code: string | null;
  name: string | null;
  origin: string | null;
  createdBy: string | null;
  createdAt: string | null;
};

export type LinksPage = {
  source: string;
  entity: string;
  total: number;
  rows: LinkRow[];
  missing: { externalId: string; rows: number; runId: string }[];
};

export type RecordHit = { id: string; code: string | null; name: string | null; note: string | null };

export type UploadRow = {
  line: number;
  externalId: string;
  code: string;
  recordId: string | null;
  name: string | null;
  current: string | null;
  action: string;
};

export type UploadReport = {
  source: string;
  entity: string;
  rows: UploadRow[];
  counts: Record<string, number>;
  saved: number;
};

// --------------------------------------------------------------------------- calls

function json(method: string, body: unknown): RequestInit {
  return { method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) };
}

/** A call that answers with no body (204). */
async function noContent(path: string, init: RequestInit, f: typeof fetch = fetch): Promise<void> {
  const response = await apiFetch(`${getAdminApiBaseUrl()}${path}`, init, f);
  if (response.ok) return;
  let message = `${init.method ?? "GET"} ${path} failed: ${response.status}`;
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string") message = body.detail;
  } catch {
    // not JSON; keep the default words
  }
  throw new ApiError(message, response.status);
}

function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  return search.size > 0 ? `?${search}` : "";
}

const one = (code: string) => `/integrations/${enc(code)}`;

export const listIntegrations = (event?: string, f: typeof fetch = fetch) =>
  requestJson<Integration[]>(`/integrations${query({ event })}`, undefined, f);

export const getIntegration = (code: string, f: typeof fetch = fetch) =>
  requestJson<IntegrationDetail>(one(code), undefined, f);

export const createIntegration = (body: NewIntegration, f: typeof fetch = fetch) =>
  requestJson<IntegrationDetail>("/integrations", json("POST", body), f);

export const updateIntegration = (code: string, body: IntegrationPatch, f: typeof fetch = fetch) =>
  requestJson<IntegrationDetail>(one(code), json("PATCH", body), f);

export const deleteIntegration = (code: string, f: typeof fetch = fetch) =>
  noContent(one(code), { method: "DELETE" }, f);

export const getFields = (code: string, f: typeof fetch = fetch) =>
  requestJson<FieldsInfo>(`${one(code)}/fields`, undefined, f);

export type ReadWith = { settings?: Settings; run?: string | null; limit?: number };

export const getSample = (code: string, body: ReadWith, f: typeof fetch = fetch) =>
  requestJson<Sample>(`${one(code)}/sample`, json("POST", body), f);

export const getPreview = (code: string, body: ReadWith & { field?: string | null }, f: typeof fetch = fetch) =>
  requestJson<Preview>(`${one(code)}/preview`, json("POST", body), f);

export const getSuggestions = (code: string, body: ReadWith, f: typeof fetch = fetch) =>
  requestJson<Suggested>(`${one(code)}/suggest`, json("POST", body), f);

export const saveDraft = (
  code: string,
  body: { settings: Settings; note?: string | null; baseUpdatedAt?: string | null },
  f: typeof fetch = fetch,
) => requestJson<IntegrationDetail>(`${one(code)}/draft`, json("PUT", body), f);

export const publishDraft = (code: string, note?: string | null, f: typeof fetch = fetch) =>
  requestJson<IntegrationDetail>(`${one(code)}/publish`, json("POST", { note: note ?? null }), f);

export const listVersions = (code: string, f: typeof fetch = fetch) =>
  requestJson<Version[]>(`${one(code)}/versions`, undefined, f);

export const restoreVersion = (code: string, number: number, f: typeof fetch = fetch) =>
  requestJson<IntegrationDetail>(`${one(code)}/versions/${number}/restore`, { method: "POST" }, f);

export const tryVersion = (
  code: string,
  body: { version?: number | null; run?: string | null; now?: boolean },
  f: typeof fetch = fetch,
) => requestJson<QueueJob>(`${one(code)}/try`, json("POST", body), f);

export const runNow = (code: string, f: typeof fetch = fetch) =>
  requestJson<QueueJob>(`${one(code)}/run`, { method: "POST" }, f);

export const pullNow = (code: string, body: { run?: string | null; now?: boolean }, f: typeof fetch = fetch) =>
  requestJson<QueueJob>(`${one(code)}/pull`, json("POST", body), f);

export const replayRun = (code: string, body: { run: string; version?: number | null }, f: typeof fetch = fetch) =>
  requestJson<QueueJob>(`${one(code)}/replay`, json("POST", body), f);

export const listRuns = (code: string, limit = 30, f: typeof fetch = fetch) =>
  requestJson<Run[]>(`${one(code)}/runs${query({ limit })}`, undefined, f);

export const getRun = (
  code: string,
  runId: string,
  options: { outcome?: string | null; limit?: number; offset?: number; raw?: boolean; q?: string | null } = {},
  f: typeof fetch = fetch,
) => requestJson<RunDetail>(`${one(code)}/runs/${enc(runId)}${query(options)}`, undefined, f);

export const listQueue = (code: string, limit = 30, f: typeof fetch = fetch) =>
  requestJson<QueueJob[]>(`${one(code)}/jobs${query({ limit })}`, undefined, f);

export const listAnswers = (code: string, state = "open", f: typeof fetch = fetch) =>
  requestJson<Answer[]>(`${one(code)}/answers${query({ state })}`, undefined, f);

export const answerQuestion = (
  code: string,
  answerId: string,
  body: { dismiss?: boolean; answer?: Record<string, unknown> | null },
  f: typeof fetch = fetch,
) => requestJson<Answer>(`${one(code)}/answers/${enc(answerId)}`, json("POST", body), f);

export const connectDoorbell = (code: string, omniumUrl: string, f: typeof fetch = fetch) =>
  requestJson<{ webhookId: string; url: string }>(`${one(code)}/doorbell`, json("POST", { omniumUrl }), f);

// Scout, through omnium

export const listScoutJobs = (base: string, f: typeof fetch = fetch) =>
  requestJson<ScoutJob[]>(`/integrations/scout/jobs${query({ base })}`, undefined, f);

export const getScoutJob = (base: string, jobId: string, f: typeof fetch = fetch) =>
  requestJson<ScoutJobDetail>(`/integrations/scout/jobs/${enc(jobId)}${query({ base })}`, undefined, f);

export const buildScraper = (
  body: { scoutBaseUrl: string; name: string; websiteUrl: string; notes: string; importCommand?: string | null },
  f: typeof fetch = fetch,
) => requestJson<{ jobId: string; name: string; exploring: boolean }>("/integrations/scout/jobs", json("POST", body), f);

export const readJobChat = (base: string, jobId: string, f: typeof fetch = fetch) =>
  requestJson<ChatMessage[]>(`/integrations/scout/jobs/${enc(jobId)}/chat${query({ base })}`, undefined, f);

export const sendJobChat = (base: string, jobId: string, content: string, f: typeof fetch = fetch) =>
  requestJson<Record<string, unknown>>(
    `/integrations/scout/jobs/${enc(jobId)}/chat${query({ base })}`,
    json("POST", { content }),
    f,
  );

export const readJobApprovals = (base: string, jobId: string, f: typeof fetch = fetch) =>
  requestJson<Approval[]>(`/integrations/scout/jobs/${enc(jobId)}/approvals${query({ base, pending: true })}`, undefined, f);

export const decideJobApproval = (
  base: string,
  approvalId: string,
  body: { approved?: boolean | null; answer?: string | null },
  f: typeof fetch = fetch,
) =>
  requestJson<Record<string, unknown>>(
    `/integrations/scout/approvals/${enc(approvalId)}${query({ base })}`,
    json("POST", body),
    f,
  );

export const getIntegrationScoutJob = (code: string, f: typeof fetch = fetch) =>
  requestJson<ScoutJobDetail>(`${one(code)}/scout/job`, undefined, f);

export const getScoutSchedule = (code: string, f: typeof fetch = fetch) =>
  requestJson<ScoutSchedule | null>(`${one(code)}/scout/schedule`, undefined, f);

export const setScoutSchedule = (code: string, cron: string, timezone: string, f: typeof fetch = fetch) =>
  requestJson<ScoutSchedule>(`${one(code)}/scout/schedule`, json("PUT", { cron, timezone }), f);

export const clearScoutSchedule = (code: string, f: typeof fetch = fetch) =>
  noContent(`${one(code)}/scout/schedule`, { method: "DELETE" }, f);

export const listScoutRuns = (code: string, limit = 20, f: typeof fetch = fetch) =>
  requestJson<{ runs: ScoutRunJoined[]; trouble: string | null }>(`${one(code)}/scout/runs${query({ limit })}`, undefined, f);

export const getRunLog = (code: string, scoutRunId: string, f: typeof fetch = fetch) =>
  requestJson<RunLog>(`${one(code)}/scout/runs/${enc(scoutRunId)}/log`, undefined, f);

export const readChat = (code: string, f: typeof fetch = fetch) =>
  requestJson<ChatMessage[]>(`${one(code)}/scout/chat`, undefined, f);

export const sendChat = (code: string, content: string, f: typeof fetch = fetch) =>
  requestJson<Record<string, unknown>>(`${one(code)}/scout/chat`, json("POST", { content }), f);

export const readApprovals = (code: string, f: typeof fetch = fetch) =>
  requestJson<Approval[]>(`${one(code)}/scout/approvals`, undefined, f);

export const decideApproval = (
  code: string,
  approvalId: string,
  body: { approved?: boolean | null; answer?: string | null },
  f: typeof fetch = fetch,
) => requestJson<Record<string, unknown>>(`${one(code)}/scout/approvals/${enc(approvalId)}`, json("POST", body), f);

// Id links

export const listLinks = (
  code: string,
  options: { entity: LinkEntity; q?: string; limit?: number; offset?: number },
  f: typeof fetch = fetch,
) => requestJson<LinksPage>(`${one(code)}/links${query(options)}`, undefined, f);

export const searchRecords = (
  code: string,
  options: { entity: LinkEntity; q?: string; limit?: number },
  f: typeof fetch = fetch,
) => requestJson<RecordHit[]>(`${one(code)}/records${query(options)}`, undefined, f);

export const linkId = (
  code: string,
  body: { entity: LinkEntity; externalId: string; recordId: string; replace?: boolean },
  f: typeof fetch = fetch,
) => requestJson<Record<string, unknown>>(`${one(code)}/links`, json("POST", body), f);

export const uploadLinks = (
  code: string,
  body: { entity: LinkEntity; rows: { externalId: string; code: string }[]; save: boolean; replace: boolean },
  f: typeof fetch = fetch,
) => requestJson<UploadReport>(`${one(code)}/links/upload`, json("POST", body), f);

/** Whether an error means Scout (not omnium) did not answer. */
export function isScoutDown(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 502 || /could not reach scout|scout answered 5/i.test(error.message);
  }
  return false;
}
